import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles, useTheme } from '@lifeomic/chroma-react/styles';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Box } from '@lifeomic/chroma-react/components/Box';
import MarkdownViewer from '@lifeomic/phc-web-toolkit/src/components/MarkdownViewer';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import moment from 'moment';
import { memberDetailMessages } from './messages';
import { useDataset } from '../../../../util/api';
import { usePractitionerNames } from '../../../../util/hooks/usePractitionerNames';

const FOUNTAIN_VISIT_NOTE_SYSTEM =
  'http://lifeomic.com/fhir/fountain-visit-note';

const FOUNTAIN_VISIT_NOTE_CODE_MAPPINGS: Record<string, string> = {
  nonvisit: 'Non-Visit Note',
  visit: 'Visit Note',
  email: 'Email Note',
};

const isElationVisitNote = (resource: fhir.Communication): boolean =>
  resource.meta?.tag?.some(tag => tag.system === FOUNTAIN_VISIT_NOTE_SYSTEM) ??
  false;

const getCommunicationContentString = (resource: fhir.Communication): string =>
  (resource.payload?.[0] as { contentString?: string } | undefined)
    ?.contentString ?? '';

// Exclude survey export notes: payload.contentString starts with this text.
// These are exported notes that are not useful to show here; consider a better
// way to hide them, or can we stop sending these to Elation now that we have
// Zori notifications?
const SURVEY_EXPORT_NOTE_CONTENT_PREFIX = 'Use the link below to see survey';

const isSurveyExportNote = (resource: fhir.Communication): boolean =>
  getCommunicationContentString(resource).startsWith(
    SURVEY_EXPORT_NOTE_CONTENT_PREFIX,
  );

const getElationVisitNoteType = (resource: fhir.Communication): string => {
  const code = resource.meta?.tag?.find(
    tag => tag.system === FOUNTAIN_VISIT_NOTE_SYSTEM,
  )?.code;
  return (code && FOUNTAIN_VISIT_NOTE_CODE_MAPPINGS[code]) || code || '—';
};

const getElationVisitNoteCode = (resource: fhir.Communication): string =>
  resource.meta?.tag?.find(tag => tag.system === FOUNTAIN_VISIT_NOTE_SYSTEM)
    ?.code ?? '';

const getSenderPractitionerId = (
  resource: fhir.Communication,
): string | null => {
  const ref = resource.sender?.reference;
  if (!ref || !ref.startsWith('Practitioner/')) return null;
  return ref.slice('Practitioner/'.length) || null;
};

const getCommunicationMemberVisible = (
  resource: fhir.Communication,
): string => {
  const isClinicOnly = resource.meta?.tag?.some(
    tag =>
      tag.system === 'http://lifeomic.com/fhir/resource-visibility' &&
      tag.code === 'clinic-only',
  );
  const isEmailType =
    getElationVisitNoteCode(resource).toLowerCase() === 'email';
  return !isClinicOnly && isEmailType ? 'Yes' : 'No';
};

type NotesRow = {
  id: string;
  resource: fhir.Communication;
  author: string;
  edited: string;
  date: string;
  memberVisible: string;
  type: string;
};

type NoteViewModalProps = {
  resource: fhir.Communication;
  onDismiss: () => void;
};

const NoteViewModal: React.FC<NoteViewModalProps> = ({
  resource,
  onDismiss,
}) => {
  const theme = useTheme();
  const title = getElationVisitNoteType(resource);
  const contentString = getCommunicationContentString(resource);

  return (
    <Modal
      isOpen={true}
      size={1}
      onDismiss={onDismiss}
      title={title}
      data-testid="note-view-modal"
    >
      <Box padding={4} style={{ fontFamily: theme.typography.fontFamily }}>
        <MarkdownViewer markdown={contentString} />
      </Box>
    </Modal>
  );
};

const communicationFhirService = new FhirService('Communication');

const useStyles = makeStyles(theme => ({
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.common.white,
    position: 'relative',
  },
}));

export type NotesContentProps = {
  memberId: string;
  fallback: React.ReactNode;
};

export const NotesContent: React.FC<NotesContentProps> = ({
  memberId,
  fallback,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const accountId = useActiveAccountId();
  const auth = useServiceAuth();
  const dataset = useDataset();
  const [fetchCommunications, fetchState] = useService(
    communicationFhirService.get,
  );
  const [communications, setCommunications] = useState<fhir.Communication[]>(
    [],
  );
  const [selectedNote, setSelectedNote] = useState<fhir.Communication | null>(
    null,
  );

  const loadCommunications = useCallback(async () => {
    if (!accountId || !memberId || !auth) return;
    try {
      const response = await fetchCommunications(accountId, {
        subject: `Patient/${memberId}`,
        _tag: [dataset.codingToken],
        _count: 200,
      });
      const entry = response?.data?.entry ?? [];
      const resources = entry
        .map((e: { resource?: fhir.Communication }) => e.resource)
        .filter(Boolean) as fhir.Communication[];
      setCommunications(resources);
    } catch {
      setCommunications([]);
    }
  }, [accountId, memberId, auth, fetchCommunications, dataset.codingToken]);

  useEffect(() => {
    void loadCommunications();
  }, [loadCommunications]);

  const elationVisitNotes = useMemo(
    () =>
      communications
        .filter(isElationVisitNote)
        .filter(note => !isSurveyExportNote(note)),
    [communications],
  );

  const senderPractitionerIds = useMemo(() => {
    const ids = new Set<string>();
    elationVisitNotes.forEach(resource => {
      const id = getSenderPractitionerId(resource);
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }, [elationVisitNotes]);

  const practitionerNames = usePractitionerNames(senderPractitionerIds);

  const rows: NotesRow[] = elationVisitNotes.map(resource => {
    const senderId = getSenderPractitionerId(resource);
    return {
      id: resource.id ?? '',
      resource,
      author: (senderId && practitionerNames[senderId]) ?? '—',
      edited: resource.meta?.lastUpdated
        ? moment(resource.meta.lastUpdated).format('M/D/YYYY h:mm A')
        : '—',
      date: resource.sent ? moment(resource.sent).format('M/D/YYYY') : '—',
      memberVisible: getCommunicationMemberVisible(resource),
      type: getElationVisitNoteType(resource),
    };
  });

  const columns = [
    {
      header: { label: formatMessage(memberDetailMessages.notesType) },
      cell: { content: (row: NotesRow) => row.type },
    },
    {
      header: { label: formatMessage(memberDetailMessages.notesAuthor) },
      cell: { content: (row: NotesRow) => row.author },
    },
    {
      header: { label: formatMessage(memberDetailMessages.notesEdited) },
      cell: { content: (row: NotesRow) => row.edited },
    },
    {
      header: { label: formatMessage(memberDetailMessages.notesDate) },
      cell: { content: (row: NotesRow) => row.date },
    },
    {
      header: { label: formatMessage(memberDetailMessages.notesMemberVisible) },
      cell: { content: (row: NotesRow) => row.memberVisible },
    },
  ];

  const handleRowClick = (row: NotesRow) => {
    setSelectedNote(row.resource);
  };

  if (fetchState.isFetching && communications.length === 0) {
    return <>{fallback}</>;
  }

  return (
    <div className={classes.tabPanel}>
      <TableModule
        isLoading={fetchState.isFetching}
        config={columns}
        data={rows}
        noResultsMessage={formatMessage(memberDetailMessages.notesEmpty)}
        onRowClick={handleRowClick}
        rowClickLabel={formatMessage(pwtSharedMessages.viewDetails)}
        rowRole="link"
      />
      {selectedNote && (
        <NoteViewModal
          resource={selectedNote}
          onDismiss={() => setSelectedNote(null)}
        />
      )}
    </div>
  );
};
